import React from 'react';
import loadable from '@loadable/component';
import useProductImpressions from '../../../hooks/useProductImpressionsV2';
import withPageComponents from '../../../page/components/with-page-components';
import { Helpers } from '../../../core/src/helpers';
import { useProductCards } from '../../../hooks/useProductProps';
import useTestFlags from '../../../abtest/useTestFlags';

const SECTION_ID = 'perfectPairing';

export const SoftBundlePlaceholder = ({ id = SECTION_ID }) => {
    return <section id={id} style={{ minHeight: '400px' }}></section>;
};

const SoftBundle = loadable(() => Helpers.retryFunc(() => import('./soft-bundle-container')), {
    fallback: <SoftBundlePlaceholder />,
});

/**
 * Perfect Pairing - Soft Bundle component for PDP
 * Displays recommended products that can be bundled with the main product
 */
export default withPageComponents(
    ({ productInitial }) => {
        const softBundleCatentryIds = productInitial?.softBundleCatentryIds || [];

        // 1. Merge all IDs into a single array
        const allIds = [{ catEntryId: productInitial?.catentryId }, ...softBundleCatentryIds];

        // 2. Make one API call for everything
        const { productCards, prices } = useProductCards(allIds, { filterOOS: true });

        // 3. Manually separate the data
        // Find the main product's price from the combined prices object
        const mainProductPrice = prices?.[productInitial?.sku];

        // Filter out the main product from the display list if you only want bundle cards
        const bundleProductCards = productCards?.filter(card => card.sku !== productInitial?.sku);

        const softBundlesWithAnalytics = useProductImpressions({
            products: bundleProductCards,
            list: 'pdp',
            componentKey: 'pdpSoftBundleAcc',
            key: productInitial?.sku,
        });

        // SORTING based on the sbRank
        const sortedBundlesWithAnalytics = softBundlesWithAnalytics?.sort((a, b) => {
            const itemA = softBundleCatentryIds.find(item => String(item.catentryId) === String(a.catentryId));
            const itemB = softBundleCatentryIds.find(item => String(item.catentryId) === String(b.catentryId));
            const rankA = parseInt(itemA?.sbRank || 0, 10);
            const rankB = parseInt(itemB?.sbRank || 0, 10);

            return rankA - rankB;
        });

        const { softBundles } = useTestFlags(['softBundles']);
        const { enabled } = softBundles || {};

        // Don't render if no soft bundle data or if the feature is not enabled
        if (
            !enabled ||
            softBundleCatentryIds.length === 0 ||
            !sortedBundlesWithAnalytics ||
            productInitial?.product_type === 'CTO'
        ) {
            return null;
        }

        return (
            <SoftBundle
                bundleProducts={sortedBundlesWithAnalytics}
                prices={prices}
                mainProduct={productInitial}
                mainProductPrice={mainProductPrice}
                id={SECTION_ID}
            />
        );
    },
    { components: ['productInitial'] },
);
